import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

export interface GpsTrip {
  id: string;
  device_id: string;
  vehicle_id?: string;
  start_time: string;
  end_time: string;
  distance_km: number;
  duration_minutes: number;
  idle_time_minutes: number;
  detection_type?: 'ignition_movement' | 'continuous_movement';
  start_location: {
    lat: number;
    lon: number;
    address?: {
      formatted?: string;
      street?: string;
      number?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
    };
  };
  end_location: {
    lat: number;
    lon: number;
    address?: {
      formatted?: string;
      street?: string;
      number?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
    };
  };
  // Join data
  device_name?: string;
  vehicle_plate?: string;
  vehicle_make?: string;
  vehicle_model?: string;
}

export interface TripsFilters {
  startDate?: string;
  endDate?: string;
  vehicleId?: string;
  deviceId?: string;
  minDistance?: number;
  maxDistance?: number;
}

export function useGpsTrips(companyId?: string, filters?: TripsFilters) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['gps-trips', companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];

      const { data, error } = await supabase.rpc('get_gps_trips_with_details', {
        p_company_id: companyId,
        p_start_date: filters?.startDate || null,
        p_end_date: filters?.endDate || null,
        p_vehicle_id: filters?.vehicleId || null,
        p_device_id: filters?.deviceId || null,
        p_min_distance: filters?.minDistance || null,
        p_max_distance: filters?.maxDistance || null
      });

      if (error) {
        console.error('Error fetching GPS trips:', error);
        throw error;
      }

      return (data || []) as GpsTrip[];
    },
    enabled: !!companyId && !!user,
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });
}

export function useGpsTripDetails(tripId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['gps-trip-details', tripId],
    queryFn: async () => {
      if (!tripId) return [];

      const { data, error } = await supabase.rpc('get_gps_trip_positions', {
        p_trip_id: tripId
      });

      if (error) {
        console.error('Error fetching trip positions:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!tripId && !!user,
  });
}

// Hook for trip replay - fetches ALL positions between start and end time
export function useGpsTripReplay(tripId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['gps-trip-replay', tripId],
    queryFn: async () => {
      if (!tripId) return [];

      const { data, error } = await (supabase as any).rpc('get_gps_trip_replay_positions', {
        p_trip_id: tripId
      });

      if (error) {
        console.error('Error fetching trip replay positions:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!tripId && !!user,
  });
}